<template>
  <div>
    <dropzone
      v-if="shouldShowDropzone"
      :url="url"
      ref="dropzone"
      :data-dropzone-id="dropzoneId"
      :name="name"
      :property-client-id="propertyClientId"
      :upload-multiple="false"
      :autoProcessQueue="false"
      :maxFiles="maxFiles"
      :disabled="disabled"
      v-tooltip.wrap="tooltipIfDisabled"
      :class="commonEntriesPropertiesCssClasses"
      v-on:file-added="increaseCountForValidation"
      v-on:file-removed="decreaseCountForValidation"
    >
    </dropzone>

    <div class="row">
      <ul class="upload-list col-12">
        <li v-for="upload in uploads" :key="upload.id">
          <a :href="pathForUpload(upload)" target="_blank">{{ upload.filename }} ({{ fileSizeMB(upload) }})</a>
          <div class="cta">
            <a :href="pathForUpload(upload)" target="_blank" class="open">open/download</a>
            <a v-if="!disabled"
              class="delete"
              @click.prevent="queueUploadForDeletion(upload)"
              :class="commonEntriesPropertiesCssClasses"
              :disabled="disabled"
              v-tooltip="tooltipIfDisabled"
            >
              delete
            </a>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import Dropzone from "../../../common/components/dropzone.vue";
  import objectModelEntryPropertiesMixin from "../../mixins/objectModelEntries/objectModelEntryPropertiesMixin";
  import ObjectModelEntry from "~/models/objectModelEntries/objectModelEntry";
  import ListViewImageUpload from "~/models/listViews/listViewImageUpload";
  import {FileAttachmentType} from "~/enums/fileAttachmentType";
  import Guid from "~/helpers/guid"

  export default Vue.extend({
    props: {
      entry: {
        type: Object as () => ObjectModelEntry,
        required: true
      },
      dropzoneId: {
        type: String,
        default() {
          return Guid.create()
        },
      },
    },
    mixins: [objectModelEntryPropertiesMixin],
    components: {
      Dropzone
    },
    data() {
      return {
        entryFileUploads: this.entry.file_uploads
      }
    },
    computed: {
      url() {
        return "/object_models/" + this.objectModel.object_model_id + "/entries/" + this.entry.id + "/file_uploads";
      },
      uploads() {
        if (!this.entryFileUploads) return null;
        return this.entryFileUploads.filter(upload => {
          return upload.property_client_id === this.propertyClientId;
        });
      },
      maxFiles() {
        if (this.objectModelProperty.file_attachment_type === FileAttachmentType.MultipleFiles) {
          return null;
        } else {
          if (!this.entry.file_uploads) return 1;
          const uploadsForProperty = this.entry.file_uploads.filter(upload => {
            return upload.property_client_id === this.objectModelProperty.client_id
          }).length;
          return 1 - uploadsForProperty;
        }
      },
      shouldShowDropzone() {
        if (this.objectModelProperty.file_attachment_type === FileAttachmentType.MultipleFiles) {
          return true;
        }

        if (!this.entry.file_uploads) return true;

        if (!this.entry.file_uploads.find(upload => upload.property_client_id === this.objectModelProperty.client_id)) {
          return true;
        }

        return false;
      },
      name() {
        return this.objectModelProperty.label_title ? this.objectModelProperty.label_title : 'attachments';
      }
    },
    methods: {
      pathForUpload(upload): string {
        return this.url + '/' + upload.id;
      },
      fileSizeMB(upload) {
        return upload.file_size;
      },
      queueUploadForDeletion(upload): void {
        if (this.disabled) return
        this.$emit('queue-upload-for-deletion', upload);
        this.removeUploadFromUI(upload);
      },
      removeUploadFromUI(upload): void {
        const uploadIndex = this.entryFileUploads.findIndex(entryUpload => entryUpload.id === upload.id);
        this.entryFileUploads.splice(uploadIndex, 1);
      },
      increaseCountForValidation(file): void {
        if (this.objectModelProperty.is_required && !this.fileTooBig(file)) {
          if (!this.entry[`f_${this.propertyClientId}`]) {
            this.entry[`f_${this.propertyClientId}`] = 0;
          }
          this.entry[`f_${this.propertyClientId}`]++;

          this.revalidate()
        }
      },
      decreaseCountForValidation(): void {
        if (this.entry[`f_${this.propertyClientId}`]) {
          this.entry[`f_${this.propertyClientId}`]--;
        }

        this.revalidate()
      },
      revalidate(): void {
        if (this.entry.validator) {
          this.entry.validate()
        }
      },
      fileTooBig(file): boolean {
        const maxSize = this.$refs.dropzone.dropzoneOptions.maxFilesize;
        const fileSizeInMB = file.size / 1000000;
        return fileSizeInMB > maxSize;
      },
    },
  })
</script>